import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { useDrag } from "react-dnd";
import { useState } from "react";
import { IconType } from "react-icons/lib";

export const ItemType = {
  NODE: "node",
};

export interface INodeNav {
  label: string;
  icon: IconType;
  link: string;
  active: boolean;
  width: number;
  height: number;
  enabled: boolean;
  subNavs: INodeSubNav[];
}

export interface INodeSubNav {
  label: string;
  icon: IconType;
  nodeTypCd: string;
  shape: string;
  iconStyle?: any;
  image: number;
  link: string;
  active: boolean;
  width: number;
  height: number;
  enabled: boolean;
}

export interface ISidebarProps {
  navs: INodeNav[];
  onLogout: () => void;
  onHelp: () => void;
  onCategoryClick: (categoryLabel: string) => void;
  onSubNavClick: (subNavLabel: string) => void;
}

interface INavItem {
  children: React.ReactNode;
  className?: string;
  active: boolean;
}

const DraggableSubNav: React.FC<{ subnav: INodeSubNav }> = ({ subnav }) => {
  const [, ref] = useDrag(
    () => ({
      type: ItemType.NODE,
      item: {
        id: subnav.label,
        label: subnav.label,
        shape: subnav.shape,
        width: subnav.width,
        height: subnav.height,
        image: subnav.image,
        nodeTypCd: subnav.nodeTypCd,
      },
    }),
    [subnav]
  );

  return (
    <Flex
      ref={ref}
      m={1}
      borderWidth={"1px"}
      borderColor={"brand.300"}
      borderRadius={"md"}
      cursor="grab"
      bg="rgba(255, 255, 255, 0.1)"
      width={"100%"}
      p={0.5}
      px={1}
    >
      <NavItem
        className={`Sidebar__Container--Nav--subnavs-${subnav.label}`}
        active={subnav.active}
      >
        <Icon
          as={subnav.icon as React.ElementType}
          boxSize="14px"
          sx={subnav.iconStyle}
          color="white"
        />
        <Text fontWeight="medium" fontSize="12px" color="white">
          {subnav.label}
        </Text>
      </NavItem>
    </Flex>
  );
};

// Sidebar Component
export const NodeSidebar: React.FC<ISidebarProps> = ({
  navs,
  onLogout,
  onHelp,
  onCategoryClick,
  onSubNavClick,
}) => {
  // Initialize the first 5 navs as open by default
  const initialOpenNavs = navs
    .slice(0, 10)
    .reduce((acc, nav) => ({ ...acc, [nav.label]: true }), {});

  const [openNavs, setOpenNavs] =
    useState<Record<string, boolean>>(initialOpenNavs);

  const toggleNav = (label: string) => {
    setOpenNavs((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  return (
    <Box
      w="fit-content"
      h="full"
      className="Sidebar"
      p={2}
      width="100%"
      position="sticky"
      overflowY="auto"
      mt={-8}
    >
      <Flex
        direction="column"
        className="Sidebar__Container"
        justifyContent="space-between"
        height="full"
      >
        <Flex
          direction="column"
          className="Sidebar__Container--Navs"
          mt={5}
          gap={4}
          overflowY="auto"
          maxHeight="calc(100vh - 50px)"
          sx={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "&": {
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            },
          }}
        >
          {navs
            .filter((nav) => nav.enabled)
            .map((nav, index) => (
              <Box color="gray.100" key={index} borderRadius={"md"}>
                <Flex
                  onClick={() => toggleNav(nav.label)}
                  background={
                    openNavs[nav.label]
                      ? "linear-gradient(to right, #7652f7, #9500ff)"
                      : "gray.100"
                  }
                  borderRadius={"md"}
                >
                  <NavItem
                    className={`Sidebar__Container--Navs-${nav.label}`}
                    active={openNavs[nav.label] || nav.active}
                  >
                    <Icon
                      as={nav.icon as React.ElementType}
                      boxSize="16px"
                      color={openNavs[nav.label] ? "white" : "gray.700"}
                    />
                    <Text
                      fontWeight={openNavs[nav.label] ? "bold" : "medium"}
                      fontSize="14px"
                      color={openNavs[nav.label] ? "white" : "gray.700"}
                    >
                      {nav.label}
                    </Text>
                  </NavItem>
                </Flex>
                {openNavs[nav.label] && (
                  <Box>
                    {nav.subNavs.map((subnav) => (
                      <Flex
                        key={subnav.label}
                        onClick={() => onSubNavClick(subnav.label)}
                      >
                        <DraggableSubNav subnav={subnav} />
                      </Flex>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
        </Flex>
      </Flex>
    </Box>
  );
};

const NavItem: React.FC<INavItem> = ({ children, className, active }) => (
  <Flex
    direction="row"
    alignItems="center"
    className={className}
    color={active ? "brand.500" : "gray.600"}
    // backgroundColor={active ? "brand.50" : "transparent"}
    _hover={{ textColor: "brand.300" }}
    borderRadius="md"
    cursor="pointer"
    width={"100%"}
    p={0.5}
    gap={1}
  >
    {children}
  </Flex>
);
